import io from "socket.io-client";
import { getAccessToken, refreshSession } from "./api";

class SocketWorker {
  constructor(companyId , userId) {
    // Um usuário/empresa novo nunca deve herdar o socket de outro login.
    if (SocketWorker.instance &&
        (String(SocketWorker.instance.companyId) !== String(companyId) ||
         String(SocketWorker.instance.userId) !== String(userId))) {
      SocketWorker.instance.disconnect();
    }
    if (!SocketWorker.instance) {
      this.companyId = companyId
      this.userId = userId
      this.socket = null;
      this.manuallyClosed = false;
      this.lastRejectedToken = null;
      this.eventListeners = {}; // Armazena os ouvintes de eventos registrados
      this.onTokenUpdated = () => {
        // Uma renovação válida permite restabelecer a conexão rejeitada.
        if (!this.manuallyClosed && this.socket && !this.socket.connected) {
          this.socket.connect();
        }
      };
      window.addEventListener("agilizachat:token-updated", this.onTokenUpdated);
      this.configureSocket();
      SocketWorker.instance = this;

    } 

    return SocketWorker.instance;
  }

  configureSocket() {
    this.socket = io(`${process.env.REACT_APP_BACKEND_URL}/${this?.companyId}` , {
      autoConnect: true,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: Infinity,
      query: { userId: this.userId },
      // O backend valida o JWT e compara a empresa autenticada ao namespace.
      // Ler o token a cada tentativa permite usar tokens renovados sem reiniciar.
      auth: cb => {
        cb({ token: getAccessToken() });
      }
    });

    this.socket.on("connect", () => {
      this.lastRejectedToken = null;
      console.log("Conectado ao servidor Socket.IO");
    });

    this.socket.on("connect_error", async error => {
      if (this.manuallyClosed) return;
      const message = error && error.message;
      console.warn("Socket.IO: conexão não autorizada ou indisponível", message);
      // Proibições entre empresas são definitivas, nunca contornadas por refresh.
      if (message !== "ERR_SOCKET_UNAUTHORIZED") return;
      const rejectedToken = getAccessToken();
      if (!rejectedToken || rejectedToken === this.lastRejectedToken) return;
      this.lastRejectedToken = rejectedToken;
      try {
        // O serviço API garante uma única renovação para todas as chamadas.
        await refreshSession();
        if (!this.manuallyClosed && this.socket && !this.socket.connected) {
          this.socket.connect();
        }
      } catch (_) {
        // Se o refresh falhar, permanecer desconectado até o próximo login.
      }
    });

    this.socket.on("disconnect", reason => {
      console.log("Desconectado do servidor Socket.IO");
      // socket.io-client já faz reconexões de rede automaticamente.
      // Uma desconexão expressa do servidor exige chamada explícita.
      if (!this.manuallyClosed && reason === "io server disconnect") {
        this.socket.connect();
      }
    });
  }

  // Adiciona um ouvinte de eventos
  on(event, callback) {
    if (this.manuallyClosed) return;
    this.connect();
    this.socket.on(event, callback);

    // Armazena o ouvinte no objeto de ouvintes
    if (!this.eventListeners[event]) {
      this.eventListeners[event] = [];
    }
    this.eventListeners[event].push(callback);
  }

  // Emite um evento
  emit(event, data) {
    if (this.manuallyClosed) return;
    this.connect();
    this.socket.emit(event, data);
  }

  // Desconecta um ou mais ouvintes de eventos
  off(event, callback) {
    // Durante o logout, cleanup de componentes não deve reabrir a conexão.
    if (this.manuallyClosed || !this.socket) {
      if (this.eventListeners) delete this.eventListeners[event];
      return;
    }
    if (this.eventListeners[event]) {
      // console.log("Desconectando do servidor Socket.IO:", event, callback);
      if (callback) {
        // Desconecta um ouvinte específico
        this.socket.off(event, callback);
        this.eventListeners[event] = this.eventListeners[event].filter(cb => cb !== callback);
      } else {
        // console.log("DELETOU EVENTOS DO SOCKET:", this.eventListeners[event]);

        // Desconecta todos os ouvintes do evento
        this.eventListeners[event].forEach(cb => this.socket.off(event, cb));
        delete this.eventListeners[event];
      }
      // console.log("EVENTOS DO SOCKET:", this.eventListeners);
    }
  }

  disconnect() {
    this.manuallyClosed = true;
    window.removeEventListener("agilizachat:token-updated", this.onTokenUpdated);
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    // A instância é estática: liberar para um próximo login com outro usuário.
    SocketWorker.instance = null;
    this.eventListeners = {};
    console.log("Socket desconectado manualmente");
  }

  reconnectAfterDelay() {
    setTimeout(() => {
      if (!this.manuallyClosed && (!this.socket || !this.socket.connected)) {
        console.log("Tentando reconectar após desconexão");
        this.connect();
      }
    }, 1000);
  }

  // Garante que o socket esteja conectado
  connect() {
    if (this.manuallyClosed) return;
    if (!this.socket) {
      this.configureSocket();
    } else if (!this.socket.connected) {
      this.socket.connect();
    }
  }

  forceReconnect() {
    if (this.manuallyClosed) return;
    this.lastRejectedToken = null;
    if (this.socket) {
      this.socket.disconnect();
      this.socket.connect();
    } else {
      this.configureSocket();
    }
  }
}

// const instance = (companyId, userId) => new SocketWorker(companyId,userId);
const instance = (companyId, userId) => new SocketWorker(companyId, userId);

export default instance;